/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-07-10 10:51:00
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import { processRes } from 'src/helpers/actions-helper';
import { getJSON } from 'redux-api-middleware';
import { I18n } from '@shopify/react-i18n';

import apiService from 'src/services/api-service';
import {
  TRIGGER_SYNC_REQUEST,
  TRIGGER_SYNC_SUCCESS,
  TRIGGER_SYNC_FAILURE,
  GET_SYNC_STATUS_REQUEST,
  GET_SYNC_STATUS_SUCCESS,
  GET_SYNC_STATUS_FAILURE,
  GET_SYNC_PROCESS_STATUS_REQUEST,
  GET_SYNC_PROCESS_STATUS_SUCCESS,
  GET_SYNC_PROCESS_STATUS_FAILURE,
  GET_AVAILABLE_SYNC_LIST_REQUEST,
  GET_AVAILABLE_SYNC_LIST_SUCCESS,
  GET_AVAILABLE_SYNC_LIST_FAILURE,
  GET_SYNC_SCHEDULE_REQUEST,
  GET_SYNC_SCHEDULE_SUCCESS,
  GET_SYNC_SCHEDULE_FAILURE,
  SAVE_SYNC_SCHEDULE_REQUEST,
  SAVE_SYNC_SCHEDULE_SUCCESS,
  SAVE_SYNC_SCHEDULE_FAILURE,
  GET_SYNC_HISTORY_REQUEST,
  GET_SYNC_HISTORY_SUCCESS,
  GET_SYNC_HISTORY_FAILURE,
  RESET_SYNC_STATUS,
  ENABLE_AUTO_SYNC_REQUEST,
  ENABLE_AUTO_SYNC_SUCCESS,
  ENABLE_AUTO_SYNC_FAILURE,
  UPDATE_SYNC_PRIORITY_REQUEST,
  UPDATE_SYNC_PRIORITY_SUCCESS,
  UPDATE_SYNC_PRIORITY_FAILURE
} from 'stores/flow-type/sync.type';
import { trackFSEvent } from 'src/integrations/fullStory';

/**
 * Trigger sync
 *
 * @param  {string} tenantId tenant id
 *
 * @return {object}          redux promise
 */
export const triggerSync = (flowName: string = '', i18n: I18n) => {
  return apiService.sendRequest('sip-v3.sync.trigger-sync', { params: { flowName } }, [
    TRIGGER_SYNC_REQUEST,
    {
      type: TRIGGER_SYNC_SUCCESS,
      payload: async (action, state, res) => {
        const result = await processRes(res, undefined, undefined, i18n);

        // Track Full Story event
        trackFSEvent('Sync Manually', {
          feature: 'Sync',
          syncType: flowName
        });
        return result;
      }
    },
    {
      type: TRIGGER_SYNC_FAILURE,
      payload: async (action, state, res) => {
        const result = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Sync.Actions.triggerSync'),
          i18n
        );
        return result;
      }
    }
  ]);
};

/**
 * get the sync status of tenant
 * @return {object} The RSAA object for API middleware
 */
export const getSyncStatus = (tenantId: string) => {
  return apiService.sendRequest('sip-v3.sync.get-sync-status', {}, [
    GET_SYNC_STATUS_REQUEST,
    {
      type: GET_SYNC_STATUS_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_SYNC_STATUS_FAILURE,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    }
  ]);
};

/**
 * get the sync status of tenant
 * @return {object} The RSAA object for API middleware
 */
export const getSyncProcessStatus = (flowName: string = '') => {
  return apiService.sendRequest('sip-v3.sync.get-sync-process-status', { params: { flowName } }, [
    GET_SYNC_PROCESS_STATUS_REQUEST,
    {
      type: GET_SYNC_PROCESS_STATUS_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_SYNC_PROCESS_STATUS_FAILURE,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    }
  ]);
};

/**
 * call to reset state
 * @return {object} The RSAA object for API middleware
 */
export const resetSyncStatus = () => {
  return { type: RESET_SYNC_STATUS };
};

/**
 * Get available sync list
 *
 * @param  {string} tenantId tenant id
 *
 * @return {object}          redux promise
 */
export const getAvailableSyncList = (i18n: I18n) => {
  return apiService.sendRequest('sip-v3.sync.get-available-sync-list', {}, [
    GET_AVAILABLE_SYNC_LIST_REQUEST,
    {
      type: GET_AVAILABLE_SYNC_LIST_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_AVAILABLE_SYNC_LIST_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Sync.Actions.getAvailableSyncList'),
          i18n
        )
    }
  ]);
};

/**
 * Get schedule sync info
 *
 * @return {object}          redux promise
 */
export const getSyncSchedule = (i18n: I18n) => {
  return apiService.sendRequest('sip-v3.sync.get-sync-schedule', {}, [
    GET_SYNC_SCHEDULE_REQUEST,
    {
      type: GET_SYNC_SCHEDULE_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_SYNC_SCHEDULE_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Sync.Actions.getSyncSchedule'),
          i18n
        )
    }
  ]);
};

/**
 * Save schedule sync info
 *
 * @return {object}          redux promise
 */
export const saveSyncSchedule = (data: Object, i18n: I18n) => {
  const { scheduleId, flowName } = data;
  let requestURL = '';
  let params = {};

  if (_.isEmpty(scheduleId)) {
    requestURL = 'sip-v3.sync.create-sync-schedule';
  } else {
    requestURL = 'sip-v3.sync.update-sync-schedule';
    params = { scheduleId };
  }
  return apiService.sendRequest(requestURL, { body: data, params }, [
    {
      type: SAVE_SYNC_SCHEDULE_REQUEST,
      payload: { flowName }
    },
    {
      type: SAVE_SYNC_SCHEDULE_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        // Track Full Story event
        trackFSEvent('Schedule Sync', {
          feature: 'Sync',
          syncType: data?.schedule?.flowName
        });
        return { data };
      }
    },
    {
      type: SAVE_SYNC_SCHEDULE_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Sync.Actions.saveSyncSchedule'),
          i18n
        );

        return errorResult;
      }
    }
  ]);
};

/**
 * Get schedule sync info
 *
 * @return {object}          redux promise
 */
export const getSyncHistory = (skip: number, limit: number, triggeredBy: string, i18n: I18n) => {
  const queries = { skip, limit, triggeredBy };
  return apiService.sendRequest('sip-v3.sync.get-sync-history', { queries }, [
    GET_SYNC_HISTORY_REQUEST,
    {
      type: GET_SYNC_HISTORY_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_SYNC_HISTORY_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Sync.Actions.getSyncHistory'),
          i18n
        )
    }
  ]);
};

/**
 * Enable/disable auto sync
 *
 * @param  {string} enable status
 *
 * @return {object}          redux promise
 */
export const enableAutoSync = (enable: Boolean = false, i18n: I18n) => {
  return apiService.sendRequest(
    'admin-v3.sync.enable-auto-sync',
    {
      body: { enable }
    },
    [
      ENABLE_AUTO_SYNC_REQUEST,
      {
        type: ENABLE_AUTO_SYNC_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: ENABLE_AUTO_SYNC_FAILURE,
        payload: async (action, state, res) => {
          const result = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Sync.Actions.triggerSync'),
            i18n
          );
          return result;
        }
      }
    ]
  );
};

/**
 * Update sync collection priority
 *
 * @return {object}          redux promise
 */
export const updateSyncCollectionPriority = (collections: Array<string | any>, i18n: I18n) => {
  return apiService.sendRequest(
    'admin-v3.sync-settings.update-collection-priority',
    {
      body: { collections }
    },
    [
      UPDATE_SYNC_PRIORITY_REQUEST,
      {
        type: UPDATE_SYNC_PRIORITY_SUCCESS,
        payload: async (action, state, res) => {
          const data = await processRes(
            res,
            'info',
            i18n.translate('AdminHTML.Sync.Actions.updateSyncCollectionPrioritySuccess'),
            i18n
          );

          // Track Full Story event
          trackFSEvent('Change Sync Collections', {
            feature: 'Sync',
            numberSyncCollections: collections.length,
            syncCollections: collections.map(e => e.title)
          });
          return { data, collections };
        }
      },
      {
        type: UPDATE_SYNC_PRIORITY_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Sync.Actions.updateSyncCollectionPriorityFailure'),
            i18n
          )
      }
    ]
  );
};
